import { useCreateTag } from "../../api/hooks/useCreateTag";
import { useDeleteTag } from "../../api/hooks/useDeleteTag";
import { useRestoreTag } from "../../api/hooks/useRestoreTag";
import { useTags } from "../../api/hooks/useTags";
import { useUpdateTag } from "../../api/hooks/useUpdateTag";
import type { components } from "../../api/schema";
import type {
  NamedResourceAdapter,
  NormalizedFireAndForget,
  NormalizedMutation,
} from "../named_resource_crud/types";

export type TagResponse = components["schemas"]["TagResponse"];

function useUpdateTagAdapted(): NormalizedMutation<{
  id: string;
  body: { name: string };
}> {
  const m = useUpdateTag();
  return {
    mutateAsync: ({ id, body }) => m.mutateAsync({ tagId: id, body }),
    isPending: m.isPending,
  };
}

function useDeleteTagAdapted(): NormalizedMutation<{ id: string }> {
  const m = useDeleteTag();
  return {
    mutateAsync: ({ id }) => m.mutateAsync({ tagId: id }),
    isPending: m.isPending,
  };
}

function useRestoreTagAdapted(): NormalizedFireAndForget<{ id: string }> {
  const m = useRestoreTag();
  return {
    mutate: ({ id }) => {
      m.mutate({ tagId: id });
    },
    isPending: m.isPending,
  };
}

export const tagsAdapter: NamedResourceAdapter<TagResponse> = {
  singularLabel: "tag",
  pluralLabel: "tags",
  pluralLabelCapitalized: "Tags",
  pageDescription: "Manage the trade tags you use to label trades.",
  emptyMessage: 'No tags yet. Click "New tag" to add one.',
  loadErrorMessage: "Failed to load tags.",
  createDescription:
    "Add a new trade tag. You can rename or delete it later.",
  editDescription:
    "Rename this tag. Trades that reference it stay attached.",
  placeholder: "momentum",
  tableTestId: "tags-table",

  useList: useTags,
  useCreate: useCreateTag,
  useUpdate: useUpdateTagAdapted,
  useDelete: useDeleteTagAdapted,
  useRestore: useRestoreTagAdapted,

  getId: (item) => item.tag_id,
  isDeleted: (item) => item.is_deleted,
  getName: (item) => item.name,
  getCreatedAt: (item) => item.created_at,
};
